import React, { useContext, useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';

import Header from "../components/Header";
import Footer from "../components/Footer";
import ImageWithChainFallback from '../components/ImageWithChainFallback'; 
import { RuntimeValuesContext } from '../context/RuntimeValuesContext';
import { useTranslation } from 'react-i18next';
import config from '../config/config.json';

// Interface for the product data
interface Product {
    id: number;
    productBrand: string;
    name: string;
    price: number;
    priceCurrency: number;
    sku: string;
    descriptionLarge: string;
    photoMain: string;
    brandPhotoMain: string;
    photo1: string;
    photo2: string;
    photo3: string;
    photo4: string;
    photo5: string;
    photo6: string;
    photo7: string;
    photo8: string;
}

function ProductDetail() {
    const { t } = useTranslation("global");
    const { runtimeValues } = useContext(RuntimeValuesContext);
    const [searchParams] = useSearchParams();
    
    const [product, setProduct] = useState<Product | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    
    const productId = searchParams.get('productId');

    useEffect(() => {
        if (!productId) {
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${config.backend.url}/api/productDetail/`, {
                    params: {
                        productId: productId,
                        currency: runtimeValues.currency
                    }
                });
                
                if (response.data) {
                    setProduct(response.data);
                    // Set the main image as the initially selected one
                    setSelectedImage(response.data.photoMain);
                }

            } catch (error) {
                console.error('Error fetching data:', error);
                setProduct(null);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [productId, runtimeValues.currency]);

    // Currency Formatter
    let formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    switch(runtimeValues.currency){
        case 'COP':{ formatter = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }); }break;
        case 'CLP':{ formatter = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }); }break;
        case 'PEN':{ formatter = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }); }break;
        case 'PYG':{ formatter = new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }); }break;
        case'UYU':{ formatter = new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU' }); }break;
        default:{ formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }); }
    }

    // --- RENDER LOGIC ---

    // 1. Show a loading state while fetching data
    if (loading) {
        return (
            <div className="app-container">
                <Header />
                <div className="detail-content" style={{ textAlign: 'center', padding: '50px' }}>
                    <p>{t("general.loadingProduct")}</p>
                </div>
                <Footer />
            </div>
        );
    }

    // 2. Show a "not found" state if the product is null after fetching
    if (!product) {
        return (
            <div className="app-container">
                <Header />
                <div className="detail-content" style={{ textAlign: 'center', padding: '50px' }}>
                    <h2>Product Not Found</h2>
                    <p>The product you are looking for does not exist or is unavailable.</p>
                    <Link to="/products" className="plpButton">Go back to Products</Link>
                </div>
                <Footer />
            </div>
        );
    }
    
    // 3. If the product exists, render the full page
    
    // Create an array of available thumbnail images
    const uniqueThumbnails = [...new Set(
        [
            product.photoMain, product.photo1, product.photo2, product.photo3,
            product.photo4, product.photo5, product.photo6, product.photo7, product.photo8
        ]
        .map(p => p?.trim()) // Trim every item in the array
        .filter((p): p is string => !!p) // Filter out any that are now empty or were null
    )];

    const imageForCart = product.photoMain || product.brandPhotoMain || '';
    const title = `${product.name} - wicaglobalgear.com`;

    return (
        <>
            <Helmet><title>{title}</title></Helmet>
            <div className="app-container">
                <Header />
                <div className="detail-content" key={product.id}>
                    <div className="product-detail">
                        <div className="detail-content-left">
                            <div className="pdp-image-main-container">
                                <ImageWithChainFallback
                                    srcChain={[
                                        selectedImage ? `images/${selectedImage}` : null, // selectedImage is already trimmed
                                        product.brandPhotoMain ? `images/${product.brandPhotoMain.trim()}` : null
                                    ]}
                                    alt={product.name}
                                    className="pdpImageMain"
                                />
                            </div>
                            {uniqueThumbnails.length > 1 && (
                                <div className="mini-thumbnails">
                                    {uniqueThumbnails.map((photo, index) => (
                                        <div 
                                            key={index} 
                                            className={`thumbnail-container ${selectedImage === photo ? 'active' : ''}`}
                                            // --- FIX: Set state with the trimmed filename ---
                                            onClick={() => setSelectedImage(photo)}
                                        >
                                            <ImageWithChainFallback
                                                srcChain={[
                                                    `images/${photo}`, // photo is already trimmed from the uniqueThumbnails array
                                                    product.brandPhotoMain ? `images/${product.brandPhotoMain.trim()}` : null
                                                ]}
                                                alt={`${product.name} thumbnail ${index + 1}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="detail-content-right">
                            <div className="pdp-info-header">
                                <h1 className="pdp-title">{product.name}</h1>
                                <Link to={`/products?brand=${encodeURIComponent(product.productBrand)}`} className="pdp-brand-link">
                                    by {product.productBrand}
                                </Link>
                            </div>
                            <div className="pdp-price-sku-block">
                                <p className='pdp-sku'>SKU {product.sku}</p>
                                <p className='pdp-price'>{formatter.format(product.priceCurrency)}</p>
                            </div>
                            <div className="pdp-actions">
                                <Link to={`/cart?productId=${product.id}&productName=${encodeURIComponent(product.name)}&productImage=${encodeURIComponent(product.photoMain || '')}&fallbackImage=${encodeURIComponent(product.brandPhotoMain || '')}&productPrice=${product.price}`} className="plpButtonFocus">
                                    {t("general.addCart")}
                                </Link>
                            </div>
                        </div>
                    </div>
                    <div className="divBasicFull" style={{marginTop: '40px'}}>
                        <h3>Product Details</h3>
                        <div dangerouslySetInnerHTML={{ __html: product.descriptionLarge }} />
                    </div>
                    <Link to="/products" className="button" style={{marginTop: '20px'}}>{t("general.backSearch")}</Link>
                </div>
                <Footer />
            </div>
        </>
    );
}

export default ProductDetail;